import React, { useEffect } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useParams } from 'react-router-dom';

import { useGetCourseByIdQuery } from '../../my-courses/coursesApi';
import { useGetUnitsByCourseIdQuery, useGetUnitByIdQuery } from '../../units/unitsApi';
import { useGetLessonsByUnitIdQuery } from '../../lessons/lessonsApi';
import { useAppSelector, useAppDispatch } from '../../../shared/hooks/redux';
import { openCreateDialog, closeCreateDialog } from '../../units/unitsSlice';
import {
  openCreateDialog as openCreateLessonDialog,
  closeCreateDialog as closeCreateLessonDialog,
} from '../../lessons/lessonsSlice';
import { closeNavDrawer } from '../../../shared/store/uiSlice';
import UnitsNav from '../../units/components/UnitsNav';
import LessonsNav from '../../lessons/components/LessonsNav';
import UnitDialog from '../../units/components/UnitDialog';
import LessonDialog from '../../lessons/components/LessonDialog';

import CurriculumNavTree from './CurriculumNavTree';

import { useObjectPermissionState } from '@/shared/hooks/useCanEdit';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';

/**
 * Single persistent layout for the entire course → unit → lesson hierarchy.
 * The sidebar drills down based on the current URL depth:
 *   /courses/:courseId               → UnitsNav
 *   /courses/:courseId/units/:unitId → LessonsNav (with back to course)
 */
const CurriculumLayout: React.FC = () => {
  const {
    courseId: courseIdParam,
    unitId,
    lessonId,
  } = useParams<{ courseId?: string; unitId?: string; lessonId?: string }>();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navDrawerOpen = useAppSelector((state) => state.ui.navDrawerOpen);

  // Unit data — fetched when drilling into a unit (allows courseId derivation for orphan routes)
  const { data: unit, isLoading: unitLoading } = useGetUnitByIdQuery(unitId!, { skip: !unitId });

  // Resolve courseId: from URL param (normal case) or from unit.course_id (orphan unit routes)
  const courseIdResolved = courseIdParam ?? unit?.course_id;

  // Course data (always needed when courseId is available)
  const { data: course } = useGetCourseByIdQuery(courseIdResolved!, { skip: !courseIdResolved });

  // Units — for UnitsNav
  const {
    data: units = [],
    error: unitsError,
    isLoading: unitsLoading,
  } = useGetUnitsByCourseIdQuery(courseIdResolved!, {
    skip: !courseIdResolved || !!unitId,
  });

  // Lessons — only needed when drilling into a unit
  const {
    data: lessons = [],
    error: lessonsError,
    isLoading: lessonsLoading,
  } = useGetLessonsByUnitIdQuery(unitId!, {
    skip: !unitId,
  });

  // Dialog state
  const { createDialogOpen: unitDialogOpen } = useAppSelector((state) => state.units);
  const { createDialogOpen: lessonDialogOpen } = useAppSelector((state) => state.lessons);
  const { user } = useAppSelector((state) => state.auth);

  const isCourseOwner = Boolean(course && user && course.owner_id === user.id);
  const coursePermission = useObjectPermissionState('course', courseIdResolved, 'editor', {
    fallbackAllowed: isCourseOwner,
  });
  const isUnitOwner = Boolean(unit && user && unit.owner_id === user.id);
  const unitPermission = useObjectPermissionState('unit', unitId, 'editor', {
    fallbackAllowed: isUnitOwner,
  });
  const canEditCourse = coursePermission.isAllowed;
  const canEditUnit = unitPermission.isAllowed;

  // Auto-close nav drawer on navigation (route param changes)
  useEffect(() => {
    dispatch(closeNavDrawer());
  }, [courseIdParam, unitId, lessonId, dispatch]);

  // Whether the user can access the parent course (data loaded successfully)
  const hasCourseAccess = Boolean(course);

  // Hide sidebar when viewing a lesson the user cannot access,
  // or when the user has lesson access but not unit access (to avoid
  // leaking parent hierarchy structure via the sidebar).
  const hasUnitAccess = !unitId || unitLoading || Boolean(unit);
  const hasCurrentLessonAccess =
    !lessonId || lessonsLoading || lessons.some((l) => l.id === lessonId);
  const showSidebar = hasCurrentLessonAccess && (hasUnitAccess || !lessonId);

  // Tree nav renders the whole course → unit → lesson hierarchy at once. It needs
  // an accessible parent course; orphan unit/lesson routes (no course access) keep
  // using the legacy drill-down nav below.
  const useTreeNav =
    isFeatureEnabled('curriculumNavTree') && Boolean(course) && Boolean(courseIdResolved);

  const sidebarContent = !showSidebar ? null : useTreeNav && course ? (
    <CurriculumNavTree
      course={course}
      canEditCourse={canEditCourse}
      currentUserId={user?.id ?? ''}
    />
  ) : unitId ? (
    <LessonsNav
      lessons={lessons}
      isLoading={lessonsLoading}
      error={lessonsError}
      canEdit={canEditUnit}
      onCreateLesson={() => dispatch(openCreateLessonDialog())}
      backTo={
        lessonId
          ? {
              label: unit?.title || 'Unit',
              path:
                courseIdResolved && hasCourseAccess
                  ? `/curriculum/courses/${courseIdResolved}/units/${unitId}`
                  : `/curriculum/units/${unitId}`,
            }
          : hasCourseAccess
            ? {
                label: course?.title || 'Course',
                path: `/curriculum/courses/${courseIdResolved}`,
              }
            : {
                label: 'Shared with Me',
                path: '/curriculum/courses?tab=shared',
              }
      }
    />
  ) : (
    <UnitsNav
      units={units}
      isLoading={unitsLoading}
      error={unitsError}
      canEdit={canEditCourse}
      onCreateUnit={() => dispatch(openCreateDialog())}
      backTo={{ label: 'My Courses', path: '/curriculum/courses' }}
    />
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: '100%' }}>
      {/* ── Mobile nav Drawer ── */}
      {sidebarContent && isMobile ? (
        <Drawer
          anchor="left"
          open={navDrawerOpen}
          onClose={() => dispatch(closeNavDrawer())}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : sidebarContent ? (
        /* ── Desktop sticky sidebar ── */
        <Box
          sx={{
            width: 260,
            flexShrink: 0,
            alignSelf: 'flex-start',
            position: 'sticky',
            top: 0,
            maxHeight: '100%',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sidebarContent}
        </Box>
      ) : null}

      {/* Full-height divider (desktop only) */}
      {sidebarContent && (
        <Box
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: '1px',
            bgcolor: 'divider',
            flexShrink: 0,
          }}
        />
      )}

      {/* ── Main content – child route renders here ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Outlet />
      </Box>

      {/* Dialogs — only render when user can edit (requires auth) */}
      {canEditCourse && (
        <UnitDialog
          open={unitDialogOpen}
          onClose={() => dispatch(closeCreateDialog())}
          courseId={courseIdResolved ?? ''}
          currentUserId={user?.id || ''}
          mode="create"
        />
      )}
      {canEditUnit && (
        <LessonDialog
          open={lessonDialogOpen}
          onClose={() => dispatch(closeCreateLessonDialog())}
          unitId={unitId ?? ''}
          currentUserId={user?.id || ''}
          mode="create"
        />
      )}
    </Box>
  );
};

export default CurriculumLayout;
